import { Text, UnstyledButton } from '@mantine/core';
import { IconPause, IconPlay } from './watch/icons';
import { fmtTime } from '../lib/text';
import { ttsPlayer, ttsProgress, useTtsPlayer } from '../lib/tts-player';

export interface TtsBarProps {
  /** px above the viewport bottom the bar must clear (56 when the mobile tab bar shows) */
  bottomOffset?: number;
  /** px the bar's centering area starts from the left (the desktop rail's width) */
  leftOffset?: number;
}

/**
 * The TTS now-playing bar (design frame 11c; 04-flows IA "Now-playing bar — for TTS audio
 * only"). Renders nothing when idle. A 44 px pill fixed at the bottom of the viewport on every
 * route — above the mobile tab bar when it shows, above the safe area on Watch — so it never
 * touches the player box (which is at the top). ▶/⏸ · Georgian title · thin progress line ·
 * time · ✕. z-index sits below Mantine's modal layer (200) so it never covers a sheet's action
 * row; it shows again when the sheet closes.
 */
export function TtsBar({ bottomOffset = 0, leftOffset = 0 }: TtsBarProps) {
  const s = useTtsPlayer();
  if (s.status === 'idle' || !s.track) return null;
  const playing = s.status === 'playing' || s.status === 'loading';
  const progress = ttsProgress(s);
  const toggle = () => (playing ? ttsPlayer.pause() : ttsPlayer.resume());
  const subtitle =
    s.status === 'error'
      ? 'Couldn’t play this'
      : s.status === 'loading'
        ? 'loading…'
        : (s.track.subtitle ?? 'TTS');
  return (
    <div
      role="region"
      aria-label="Now playing"
      style={{
        position: 'fixed',
        left: leftOffset + 12,
        right: 12,
        bottom: `calc(${bottomOffset + 12}px + env(safe-area-inset-bottom, 0px))`,
        margin: '0 auto',
        maxWidth: 560,
        height: 44,
        zIndex: 190,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 6px 0 4px',
        borderRadius: 12,
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        boxShadow: 'var(--mantine-shadow-sm)',
        overflow: 'hidden',
      }}
    >
      <UnstyledButton
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'var(--nn-accent)',
            color: 'var(--nn-on-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: s.status === 'loading' ? 0.7 : 1,
          }}
        >
          <span style={{ display: 'flex', transform: 'scale(.72)' }}>
            {playing ? <IconPause /> : <IconPlay />}
          </span>
        </span>
      </UnstyledButton>
      <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Text fz={12.5} truncate lang="ka" style={{ lineHeight: 1.25 }}>
          {s.track.title}
        </Text>
        <Text fz={10.5} c="dimmed" truncate style={{ lineHeight: 1.25 }}>
          {subtitle}
        </Text>
      </div>
      <Text
        fz={10.5}
        c="dimmed"
        style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {fmtTime(s.positionMs)}
        {s.durationMs != null ? ` / ${fmtTime(s.durationMs)}` : ''}
      </Text>
      <UnstyledButton
        onClick={() => ttsPlayer.stop()}
        aria-label="Stop"
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--nn-faint)',
          flexShrink: 0,
          fontSize: 15,
        }}
      >
        ✕
      </UnstyledButton>
      {/* thin progress line along the bottom edge */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 2,
          width: progress == null ? '0%' : `${progress * 100}%`,
          background: 'var(--nn-accent)',
          transition: 'width 250ms linear',
        }}
      />
    </div>
  );
}
